/**
 * «Подробно о воде» на каноне — `/analysis/water`.
 *
 * Вода цикла по событиям файла ирригации (agroai-0e8, №725): событие закрывает
 * полив плана. Заявок у событий нет, поэтому «запрошено» и недоподачи здесь нет —
 * экран показывает то, что есть в файле: поливы цикла (полито / ждём / без
 * подачи), каналы → хозяйства, политую землю, поливы без подачи, воду вне плана
 * и динамику по неделям. Собирает всё сервер (`/water/detail`); браузер числа
 * не считает. м³ и га не смешиваются: объём — по событиям, земля — по участкам,
 * каждый гектар один раз.
 */
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { Droplets, FileSearch, Layers, Ruler } from 'lucide-react';
import { request, type QueryParams, type Schemas } from '../../api/client';
import { useRequest } from '../../api/useRequest';
import { useAnalysisContext } from '../../analysis/context';
import { usePageTrail } from '../shell/ShellContext';
import { Title } from './shared';
import { paramsKey, RequestGate, useIrrigationChoice } from '../pages/WaterCanon';
import { Badge } from '../ui/Badge';
import { Card, Grid, Stack } from '../ui/Card';
import { ChoiceSelect } from '../ui/AnalysisControls';
import { ChartContainer, ChartTooltipContent, niceTicks, type ChartConfig } from '../ui/chart/Chart';
import { DataTable, type Column } from '../ui/DataTable';
import { EmptyState } from '../ui/EmptyState';
import { KpiCard } from '../ui/KpiCard';
import { metricById, metricText, STATE_ORDER, STATE_TONES, STATE_WORDS, isState } from '../pages/water/waterFile';
import { countOf, dateShort, haUnit, m3, m3Compact, num, windowLabel, WORDS } from '../lib/format';
import { defineMessages } from '../../i18n/define';
import { UNIT_LABELS, useT } from '../../i18n/hooks';
import { tr } from '../../i18n/runtime';
import '../pages/water/water-file.css';

type Detail = Schemas['WaterDetailOut'];
type Watering = Detail['waterings'][number];
type Farm = Detail['no_delivery_farms'][number];
type Canal = Detail['canals'][number];

/** Земля пар без подачи меньше сотой гектара — «без площади» (как на сервере, журнал №1451). */
const NO_LAND_BELOW_HA = 0.01;

const m = defineMessages({
  trailLevel: { id: 'station.waterDetail.trail.level', defaultMessage: 'Район', description: 'Хлебная крошка: уровень экрана «Подробно о воде» (район)' },
  title: { id: 'station.waterDetail.title', defaultMessage: 'Подробно о воде', description: 'Заголовок экрана и хлебной крошки: вода цикла по файлу ирригации' },
  eyebrow: { id: 'station.waterDetail.eyebrow', defaultMessage: 'ОСНОВАНИЯ / ПОЛИВ', description: 'Надзаголовок экрана заглавными: раздел «Основания», подраздел полив' },
  lead: { id: 'station.waterDetail.lead', defaultMessage: 'Поливы цикла по файлу ирригации: сколько воды подано и земли полито, по каким каналам и хозяйствам, где подачи не было, что вне плана и как шла вода по неделям.', description: 'Пояснение под заголовком экрана «Подробно о воде»' },
  backToAnalyst: { id: 'station.waterDetail.backToAnalyst', defaultMessage: 'Вернуться к аналитику', description: 'Кнопка справа от заголовка: вернуться на экран аналитика' },
  cyclesWhat: { id: 'station.waterDetail.cyclesWhat', defaultMessage: 'агроциклы сезона', description: 'Что не загрузилось (винительный падеж): список агроциклов сезона' },
  noCycles: { id: 'station.waterDetail.noCycles', defaultMessage: 'Сервер не вернул ни одного агроцикла в этой области.', description: 'Пустое состояние экрана: нет агроциклов' },
  cycleLabel: { id: 'station.waterDetail.cycleLabel', defaultMessage: 'Агроцикл', description: 'Подпись выбора агроцикла на экране «Подробно о воде»' },
  loadWhat: { id: 'station.waterDetail.loadWhat', defaultMessage: 'воду цикла по файлу ирригации', description: 'Что не загрузилось (винительный падеж): вода цикла по файлу ирригации' },
  noData: { id: 'station.waterDetail.noData', defaultMessage: 'нет данных', description: 'Вместо числа, когда сервер значения не дал (это не ноль)' },
  noFileTitle: { id: 'station.waterDetail.noFile.title', defaultMessage: 'Файлов ирригации нет', description: 'Заголовок пустого состояния: ни одного опубликованного файла ирригации' },
  kpiDelivered: { id: 'station.waterDetail.kpi.delivered', defaultMessage: 'Подано воды', description: 'Подпись KPI: объём воды по событиям файла ирригации за цикл' },
  kpiDeliveredNote: { id: 'station.waterDetail.kpi.deliveredNote', defaultMessage: '{events} · {farms}', description: 'Мелкая подпись под KPI «Подано воды»; {events} — число событий, закрывших полив плана, со словами, {farms} — число хозяйств со словом' },
  planEventsCount: { id: 'station.waterDetail.planEventsCount', defaultMessage: '{count, plural, one {# событие закрыло полив плана} few {# события закрыли полив плана} many {# событий закрыли полив плана} other {# события закрыли полив плана}}', description: 'Число событий файла ирригации, закрывших полив плана, со словами: «11 684 события закрыли полив плана»' },
  eventsCount: { id: 'station.waterDetail.eventsCount', defaultMessage: '{count, plural, one {# событие} few {# события} many {# событий} other {# события}}', description: 'Число событий файла ирригации со словом' },
  reconcile: { id: 'station.waterDetail.reconcile', defaultMessage: 'Из них: закрыли поливы плана {plan}{events} · вне плана {outside}{cycle} · на разборе {review}', description: 'Строка сверки под показателями: из чего сложено «Подано воды»; {plan} — объём событий, закрывших поливы плана, {events} — их число в скобках (или пусто), {outside} — объём вне плана с числом событий, {cycle} — объём вне цикла (или пусто), {review} — объём событий, которые ещё не закрыли ни одного полива и ждут разбора менеджера' },
  reconcileEvents: { id: 'station.waterDetail.reconcile.events', defaultMessage: ' ({events})', description: 'Число событий в скобках после объёма в строке сверки; {events} — число со словом' },
  reconcileOutside: { id: 'station.waterDetail.reconcile.outside', defaultMessage: '{volume} ({events})', description: 'Объём вне плана и в скобках число событий в строке сверки; {volume} — объём воды, {events} — число событий со словом' },
  reconcileCycle: { id: 'station.waterDetail.reconcile.cycle', defaultMessage: ' · вне цикла {volume}', description: 'Часть строки сверки: объём событий вне агроцикла; показывается, только если он есть; {volume} — объём воды' },
  reconcileTitle: { id: 'station.waterDetail.reconcile.title', defaultMessage: 'Итог «Подано воды» — все действующие события цикла (метрика «Подано по файлу ирригации»). Таблицы ниже раскладывают только события, закрывшие полив плана; остальные события названы здесь: вне плана, вне цикла и на разборе — они ещё не закрыли ни одного полива и ждут решения менеджера.', description: 'Всплывающая подсказка к строке сверки под показателями «Подробно о воде»' },
  basisPlan: { id: 'station.waterDetail.basis.plan', defaultMessage: 'События, закрывшие полив плана: {plan} из {total}', description: 'Подпись основы над таблицей (поливы, каналы, недели): считаются только события, закрывшие полив плана; {plan} — их объём, {total} — всё «Подано воды» цикла' },
  basisPlanOnly: { id: 'station.waterDetail.basis.planOnly', defaultMessage: 'События, закрывшие полив плана', description: 'Подпись основы над таблицей (поливы, каналы, недели), когда итога для сравнения нет' },
  kpiLand: { id: 'station.waterDetail.kpi.land', defaultMessage: 'Полито земли', description: 'Подпись KPI: гектары земли, где был полив по файлу' },
  kpiLandNote: { id: 'station.waterDetail.kpi.landNote', defaultMessage: 'каждый гектар один раз', description: 'Мелкая подпись под KPI «Полито земли»: земля считается без повторов' },
  kpiStates: { id: 'station.waterDetail.kpi.states', defaultMessage: 'Пары «поле × полив»', description: 'Подпись KPI: пары «поле × полив» плана цикла' },
  kpiStatesNote: { id: 'station.waterDetail.kpi.statesNote', defaultMessage: 'полито {done} · частично {partial} · ждём {waiting} · впереди {upcoming}', description: 'Мелкая подпись под KPI пар «поле × полив»; числа строкой' },
  kpiNoDelivery: { id: 'station.waterDetail.kpi.noDelivery', defaultMessage: 'Без подачи', description: 'Подпись KPI: пары «поле × полив», у которых окно закрылось без события в файле' },
  kpiNoDeliveryNote: { id: 'station.waterDetail.kpi.noDeliveryNote', defaultMessage: 'окно закрылось, события в файле нет; файл мог опоздать', description: 'Мелкая подпись под KPI «Без подачи»' },
  pairsUnit: { id: 'station.waterDetail.pairsUnit', defaultMessage: '{count, plural, one {пара} few {пары} many {пар} other {пары}}', description: 'Единица рядом с числом KPI: пары «поле × полив»' },
  wateringsTitle: { id: 'station.waterDetail.waterings.title', defaultMessage: 'Поливы цикла', description: 'Заголовок карточки: поливы цикла с состояниями, объёмом и землёй' },
  colWatering: { id: 'station.waterDetail.waterings.col.watering', defaultMessage: 'Полив', description: 'Колонка таблицы поливов цикла: название полива' },
  colWindow: { id: 'station.waterDetail.waterings.col.window', defaultMessage: 'Окно', description: 'Колонка таблицы поливов цикла: окно дат полива' },
  colStates: { id: 'station.waterDetail.waterings.col.states', defaultMessage: 'Пары по состояниям', description: 'Колонка таблицы поливов цикла: сколько пар «поле × полив» в каждом состоянии' },
  colEvents: { id: 'station.waterDetail.waterings.col.events', defaultMessage: 'Событий', description: 'Колонка таблицы поливов цикла: число событий файла; числовая колонка' },
  colVolume: { id: 'station.waterDetail.waterings.col.volume', defaultMessage: 'Подано', description: 'Колонка таблицы: объём воды по файлу, м³; числовая колонка' },
  colLand: { id: 'station.waterDetail.waterings.col.land', defaultMessage: 'Полито', description: 'Колонка таблицы: политая земля, га; числовая колонка' },
  colFarms: { id: 'station.waterDetail.waterings.col.farms', defaultMessage: 'Хозяйств', description: 'Колонка таблицы поливов цикла: число хозяйств полива; числовая колонка' },
  wateringsEmptyTitle: { id: 'station.waterDetail.waterings.emptyTitle', defaultMessage: 'Поливов в цикле нет', description: 'Заголовок пустого состояния таблицы поливов цикла' },
  wateringsEmptyReason: { id: 'station.waterDetail.waterings.emptyReason', defaultMessage: 'в этом агроцикле у выбранной области нет работ полива — у зерновых поливы часто не назначаются', description: 'Пояснение пустого состояния таблицы поливов цикла' },
  canalsTitle: { id: 'station.waterDetail.canals.title', defaultMessage: 'Каналы → хозяйства', description: 'Заголовок карточки: какие хозяйства получили воду из какого канала' },
  canalsEmpty: { id: 'station.waterDetail.canals.empty', defaultMessage: 'Событий файла ирригации с каналом в цикле нет.', description: 'Пустое состояние списка каналов' },
  canalName: { id: 'station.waterDetail.canals.name', defaultMessage: 'канал {label}', description: 'Название канала в карточке канала; {label} — имя канала из данных' },
  canalsAside: { id: 'station.waterDetail.canals.aside', defaultMessage: '{canals} · по объёму воды', description: 'Пояснение в шапке таблицы «Каналы → хозяйства»; {canals} — число каналов словом («22 канала»)' },
  canalsCount: { id: 'station.waterDetail.canals.count', defaultMessage: '{count, plural, one {# канал} few {# канала} many {# каналов} other {# канала}}', description: 'Число каналов словом: «22 канала»' },
  canalsWord: { id: 'station.waterDetail.canals.word', defaultMessage: '{count, plural, one {канал} few {канала} many {каналов} other {канала}}', description: 'Слово-знаменатель под таблицей каналов («показано 10 из 22 каналов»); без числа' },
  canalsEmptyTitle: { id: 'station.waterDetail.canals.emptyTitle', defaultMessage: 'Каналов нет', description: 'Заголовок пустого состояния таблицы «Каналы → хозяйства»' },
  colCanal: { id: 'station.waterDetail.canals.col.canal', defaultMessage: 'Канал', description: 'Колонка таблицы «Каналы → хозяйства»: канал' },
  colCanalFarms: { id: 'station.waterDetail.canals.col.farms', defaultMessage: 'Хозяйства канала', description: 'Колонка таблицы «Каналы → хозяйства»: крупнейшие хозяйства, получившие воду из канала, с объёмом' },
  farmShort: { id: 'station.waterDetail.canals.farmShort', defaultMessage: '<farm>{name}</farm> · {volume}', description: 'Хозяйство в ячейке таблицы каналов; <farm> — ссылка на карточку хозяйства, {name} — имя, {volume} — объём воды (сжато: «1,2 млн м³»)' },
  noLand: { id: 'station.waterDetail.noDelivery.noLand', defaultMessage: 'без площади', description: 'Метка в колонке «Земля» таблицы «где разобраться»: у пар без подачи нет площади (дехканские участки с почти нулевой площадью размещения); строчными' },
  noLandTitle: { id: 'station.waterDetail.noDelivery.noLandTitle', defaultMessage: 'Площадь размещения этих участков почти нулевая: пары есть, земли нет. Чаще всего это дехканские хозяйства и их сводные записи; площадь уточняется в размещении.', description: 'Всплывающая подсказка к метке «без площади» в таблице «где разобраться»' },
  noDeliveryInfo: { id: 'station.waterDetail.noDelivery.info', defaultMessage: 'Хозяйства, где больше всего земли без подачи: окно полива закрылось, события в файле нет. Порядок — по земле (га), не по числу пар; хозяйства без площади — в конце.', description: 'Пояснение «i» у таблицы «Где разобраться: земля без подачи» на экране «Подробно о воде»' },
  farmFallback: { id: 'station.waterDetail.farmFallback', defaultMessage: 'хозяйство {id}', description: 'Имя хозяйства, если сервер имя не назвал; {id} — номер' },
  farmUnknown: { id: 'station.waterDetail.canals.farmUnknown', defaultMessage: 'хозяйство не определено', description: 'Подпись строки канала, когда у события нет хозяйства' },
  moreFarms: { id: 'station.waterDetail.canals.moreFarms', defaultMessage: 'ещё {farms}', description: 'Строка под списком хозяйств канала; {farms} — число хозяйств со словом' },
  noDeliveryTitle: { id: 'station.waterDetail.where.title', defaultMessage: 'Где разобраться: земля без подачи', description: 'Заголовок карточки на экране «Подробно о воде»: хозяйства, где больше всего земли (га) осталось без подачи воды' },
  noDeliveryEmptyTitle: { id: 'station.waterDetail.noDelivery.emptyTitle', defaultMessage: 'Поливов без подачи нет', description: 'Заголовок пустого состояния списка «без подачи»' },
  noDeliveryEmptyReason: { id: 'station.waterDetail.noDelivery.emptyReason', defaultMessage: 'во всех закрывшихся окнах цикла событие в файле есть', description: 'Пояснение пустого состояния списка «без подачи»' },
  colFarm: { id: 'station.waterDetail.noDelivery.col.farm', defaultMessage: 'Хозяйство', description: 'Колонка таблицы: хозяйство' },
  colPairs: { id: 'station.waterDetail.noDelivery.col.pairs', defaultMessage: 'Без подачи', description: 'Колонка таблицы: число пар «поле × полив» без подачи; числовая колонка' },
  colNdLand: { id: 'station.waterDetail.noDelivery.col.land', defaultMessage: 'Земля', description: 'Колонка таблицы: земля пар без подачи, га; числовая колонка' },
  farmsWord: { id: 'station.waterDetail.noDelivery.farmsWord', defaultMessage: '{count, plural, one {хозяйство} few {хозяйства} many {хозяйств} other {хозяйства}}', description: 'Слово-знаменатель под таблицей «без подачи»: хозяйств; без числа' },
  weeksTitle: { id: 'station.waterDetail.weeks.title', defaultMessage: 'Динамика по неделям', description: 'Заголовок карточки: вода и политая земля по неделям' },
  weeksVolumeAria: { id: 'station.waterDetail.weeks.volumeAria', defaultMessage: 'Подано воды по неделям, м³', description: 'Подпись графика объёма по неделям для диктора' },
  weeksLandAria: { id: 'station.waterDetail.weeks.landAria', defaultMessage: 'Политая земля по неделям, га', description: 'Подпись графика земли по неделям для диктора' },
  weeksVolumeTitle: { id: 'station.waterDetail.weeks.volumeTitle', defaultMessage: 'Подано, м³ за неделю', description: 'Подзаголовок графика объёма по неделям' },
  weeksLandTitle: { id: 'station.waterDetail.weeks.landTitle', defaultMessage: 'Полито, га за неделю (участок один раз в неделе)', description: 'Подзаголовок графика земли по неделям' },
  weeksTip: { id: 'station.waterDetail.weeks.tip', defaultMessage: 'Неделя с {week}', description: 'Заголовок подсказки столбика графика; {week} — дата начала недели, коротко' },
  weeksEmpty: { id: 'station.waterDetail.weeks.empty', defaultMessage: 'Событий файла ирригации в цикле нет — недельного ряда нет.', description: 'Пустое состояние графика по неделям' },
  outsideTitle: { id: 'station.waterDetail.outside.title', defaultMessage: 'Вне плана и вне цикла', description: 'Заголовок карточки: вода без полива плана цикла' },
  outsideNote: { id: 'station.waterDetail.outside.note', defaultMessage: 'Отдельный слой района: эти события не входят в поливы цикла и не попадают в знаменатели.', description: 'Пояснение карточки «Вне плана и вне цикла»' },
  outsideUnplanned: { id: 'station.waterDetail.outside.unplanned', defaultMessage: 'Вне плана: {events}, {volume}', description: 'Строка: события полива вне плана; {events} — число событий, {volume} — объём воды' },
  outsideCycle: { id: 'station.waterDetail.outside.cycle', defaultMessage: 'Вне цикла: {volume}, земля {land}', description: 'Строка: события вне агроцикла; {volume} — объём воды, {land} — земля' },
  outsideUnmatched: { id: 'station.waterDetail.outside.unmatched', defaultMessage: 'Без земли в справочнике: {volume}', description: 'Строка: объём событий, которые не удалось привязать к земле' },
  sourcesTitle: { id: 'station.waterDetail.sources', defaultMessage: 'Как читать', description: 'Заголовок блока заметок внизу экрана: как читать числа' },
});

const NONE = () => tr(m.noData);

/** Объём события или «нет данных»: не ноль. */
const volumeText = (value: string | number | null | undefined): string => {
  const n = value === null || value === undefined ? null : Number(value);
  return n === null || !Number.isFinite(n) ? NONE() : m3(n);
};

const landText = (value: string | number | null | undefined): string => {
  const n = value === null || value === undefined ? null : Number(value);
  return n === null || !Number.isFinite(n) ? NONE() : haUnit(n);
};

const vol = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export function WaterLedgerCanon() {
  const t = useT();
  usePageTrail([{ level: t(m.trailLevel), name: t(m.title) }]);
  const { params, linkSearch } = useAnalysisContext();
  const choice = useIrrigationChoice(params);

  return (
    <div className="astra" data-guide="water">
      <Title eyebrow={t(m.eyebrow)} title={t(m.title)} text={t(m.lead)}>
        <Link className="astra-button" to={`/analytics${linkSearch()}`}>{t(m.backToAnalyst)}</Link>
      </Title>
      <RequestGate res={choice.cycles} what={t(m.cyclesWhat)}>
        {() =>
          choice.choices.length === 0 ? (
            <p className="astra-empty">{choice.cyclesEmptyNote ?? t(m.noCycles)}</p>
          ) : (
            <div className="chips" style={{ alignItems: 'flex-end' }}>
              <ChoiceSelect
                label={t(m.cycleLabel)}
                value={choice.cycle?.cycle ?? ''}
                options={choice.choices.map((c) => ({ id: c.cycle, name: c.name }))}
                onChange={choice.setCycle}
              />
            </div>
          )
        }
      </RequestGate>
      {choice.cycle ? <DetailBody params={params} cycle={choice.cycle.cycle} /> : null}
    </div>
  );
}

function DetailBody({ params, cycle }: { params: QueryParams; cycle: string }) {
  const t = useT();
  const res = useRequest(() => request<Detail>('/water/detail', { ...params, cycle }), [paramsKey(params), cycle]);
  return (
    <RequestGate res={res} what={t(m.loadWhat)}>
      {(d) => <Detailed d={d} />}
    </RequestGate>
  );
}

function farmName(f: Farm): string {
  return f.actor?.name ?? tr(m.farmFallback, { id: f.actor?.id ?? '—' });
}

/** Объём сверки: не-число — пусто (части сверки без значения не показываются). */
const part = (value: string | number | null | undefined): number | null => vol(value);

/**
 * Строка сверки под показателями: из чего сложено «Подано воды». Сервер отдаёт
 * части необязательно (нет метрики подачи — нет и сверки), поэтому без закрывших
 * полив плана и «на разборе» строки нет, а «вне цикла» — только если оно есть.
 */
function reconcileText(d: Detail): string | null {
  const { plan_volume_m3: plan, outside_plan_volume_m3: outside, outside_plan_events: outsideEvents, outside_cycle_volume_m3: cycle, review_volume_m3: review } = d.totals;
  if (part(plan) === null || part(review) === null) return null;
  return tr(m.reconcile, {
    plan: volumeText(plan),
    events: tr(m.reconcileEvents, { events: tr(m.eventsCount, { count: d.totals.events }) }),
    outside:
      outsideEvents === null || outsideEvents === undefined
        ? volumeText(outside ?? 0)
        : tr(m.reconcileOutside, { volume: volumeText(outside ?? 0), events: tr(m.eventsCount, { count: outsideEvents }) }),
    cycle: (part(cycle) ?? 0) > 0 ? tr(m.reconcileCycle, { volume: volumeText(cycle) }) : '',
    review: volumeText(review),
  });
}

/** Подпись основы таблиц поливов, каналов и недель: только события, закрывшие полив плана. */
function basisText(d: Detail): string {
  const plan = part(d.totals.plan_volume_m3);
  const total = part(d.totals.volume_m3);
  if (plan === null || total === null) return tr(m.basisPlanOnly);
  return tr(m.basisPlan, { plan: volumeText(plan), total: volumeText(total) });
}

function Detailed({ d }: { d: Detail }) {
  const t = useT();
  const noFile = d.freshness.versions === 0;
  const counts = d.totals.counts;
  const reconciliation = reconcileText(d);
  const basis = basisText(d);
  const metric = (id: string) => metricById(d.outside_plan, id);
  const text = (id: string) => {
    const found = metric(id);
    return found ? metricText(found) : NONE();
  };
  if (noFile) {
    return <EmptyState title={t(m.noFileTitle)} reason={d.freshness.note} />;
  }

  return (
    <Stack>
      <div data-guide="volume">
        <Grid cols={4}>
          <KpiCard
            label={t(m.kpiDelivered)}
            icon={Droplets}
            value={volumeText(d.totals.volume_m3)}
            note={t(m.kpiDeliveredNote, { events: t(m.planEventsCount, { count: d.totals.events }), farms: countOf(d.totals.farms, WORDS.farm) })}
          />
          <KpiCard label={t(m.kpiLand)} icon={Ruler} value={landText(d.totals.area_ha)} note={t(m.kpiLandNote)} />
          <KpiCard
            label={t(m.kpiStates)}
            icon={Layers}
            value={num(d.totals.pairs)}
            unit={t(m.pairsUnit, { count: d.totals.pairs })}
            note={t(m.kpiStatesNote, {
              done: num(counts.done ?? 0),
              partial: num(counts.partial ?? 0),
              waiting: num(counts.waiting ?? 0),
              upcoming: num(counts.upcoming ?? 0),
            })}
          />
          <KpiCard
            label={t(m.kpiNoDelivery)}
            icon={FileSearch}
            tone={(counts.no_delivery ?? 0) > 0 ? 'warning' : undefined}
            value={num(counts.no_delivery ?? 0)}
            unit={t(m.pairsUnit, { count: counts.no_delivery ?? 0 })}
            note={t(m.kpiNoDeliveryNote)}
          />
        </Grid>
      </div>
      {reconciliation ? (
        <p className="astra-footnote" data-guide="reconcile" title={t(m.reconcileTitle)}>
          {reconciliation}
        </p>
      ) : null}
      <p className="astra-footnote">
        <Badge tone="outline">{d.freshness.note}</Badge> {d.scope_note}
      </p>

      <div data-guide="waterings">
        <Card size="lg" title={t(m.wateringsTitle)}>
          <p className="t-caption" data-basis="plan-events">{basis}</p>
          <DataTable
            columns={wateringColumns()}
            rows={d.waterings}
            getKey={(w) => `${w.campaign_id}:${w.work_def_id}`}
            limit={12}
            empty={{ title: t(m.wateringsEmptyTitle), reason: t(m.wateringsEmptyReason) }}
          />
        </Card>
      </div>

      {/* Сначала — где разобраться (по земле без подачи), затем полная таблица
          «канал → хозяйства», перенесённая сюда с «Воды» (журнал №1450). */}
      <div data-guide="no-delivery">
        <Card size="lg" title={t(m.noDeliveryTitle)} info={t(m.noDeliveryInfo)}>
          <DataTable
            columns={noDeliveryColumns()}
            rows={d.no_delivery_farms}
            getKey={(f) => String(f.actor?.id ?? farmName(f))}
            denominator={m.farmsWord}
            limit={8}
            empty={{ title: t(m.noDeliveryEmptyTitle), reason: t(m.noDeliveryEmptyReason) }}
          />
        </Card>
      </div>

      <div data-guide="canals">
        <Card size="lg" title={t(m.canalsTitle)} aside={<span className="t-caption">{t(m.canalsAside, { canals: t(m.canalsCount, { count: d.canals.length }) })}</span>}>
          <p className="t-caption" data-basis="plan-events">{basis}</p>
          <DataTable
            columns={canalColumns(t)}
            rows={d.canals}
            getKey={(c) => c.label}
            denominator={m.canalsWord}
            limit={10}
            empty={{ title: t(m.canalsEmptyTitle), reason: t(m.canalsEmpty) }}
          />
        </Card>
      </div>

      <div data-guide="weeks">
        <Card size="lg" title={t(m.weeksTitle)}>
          <p className="t-caption" data-basis="plan-events">{basis}</p>
          {d.weeks.length === 0 ? <p className="t-caption">{t(m.weeksEmpty)}</p> : <Weeks weeks={d.weeks} />}
        </Card>
      </div>

      <div data-guide="outside">
        <Card size="lg" title={t(m.outsideTitle)}>
          <Stack tight>
            <p className="t-caption">{t(m.outsideNote)}</p>
            <ul className="wv-plain">
              <li>{t(m.outsideUnplanned, { events: text('water.unplanned_events'), volume: text('water.unplanned_m3') })}</li>
              <li>{t(m.outsideCycle, { volume: text('water.outside_cycle_m3'), land: text('water.outside_cycle_land_ha') })}</li>
              <li>{t(m.outsideUnmatched, { volume: text('water.unmatched_m3') })}</li>
            </ul>
          </Stack>
        </Card>
      </div>

      <Stack tight>
        {d.notes.map((n) => (
          <p key={n} className="t-caption">{n}</p>
        ))}
      </Stack>
    </Stack>
  );
}

function wateringColumns(): Column<Watering>[] {
  return [
    { key: 'name', header: tr(m.colWatering), render: (w) => w.name },
    { key: 'win', header: tr(m.colWindow), render: (w) => windowLabel(w.window_from, w.window_to) },
    {
      key: 'states',
      header: tr(m.colStates),
      render: (w) => (
        <span className="wf-step">
          {STATE_ORDER.filter((s) => (w.counts[s] ?? 0) > 0).map((s) => (
            <Badge key={s} tone={STATE_TONES[s]}>{`${STATE_WORDS[s]} ${num(w.counts[s] ?? 0)}`}</Badge>
          ))}
          {Object.keys(w.counts).filter((s) => !isState(s)).map((s) => <Badge key={s} tone="outline">{`${s} ${num(w.counts[s] ?? 0)}`}</Badge>)}
        </span>
      ),
    },
    { key: 'ev', header: tr(m.colEvents), numeric: true, render: (w) => num(w.events) },
    { key: 'vol', header: tr(m.colVolume), unit: tr(UNIT_LABELS.m3), numeric: true, render: (w) => (vol(w.volume_m3) === null ? NONE() : num(Math.round(vol(w.volume_m3)!))) },
    { key: 'land', header: tr(m.colLand), unit: tr(UNIT_LABELS.ha), numeric: true, render: (w) => (vol(w.area_ha) === null ? NONE() : num(Math.round(vol(w.area_ha)!))) },
    { key: 'farms', header: tr(m.colFarms), numeric: true, render: (w) => num(w.farms) },
  ];
}

function noDeliveryColumns(): Column<Farm>[] {
  return [
    {
      key: 'farm',
      header: tr(m.colFarm),
      render: (f) => (f.actor ? <Link to={`/farms/${f.actor.id}?tab=water`}>{farmName(f)}</Link> : farmName(f)),
    },
    {
      key: 'land',
      header: tr(m.colNdLand),
      unit: tr(UNIT_LABELS.ha),
      numeric: true,
      // Земля меньше сотой гектара — «без площади» (журнал №1451), а не «0».
      render: (f) => {
        const land = vol(f.area_ha);
        if (land !== null && land < NO_LAND_BELOW_HA) return <span title={tr(m.noLandTitle)}><Badge tone="outline">{tr(m.noLand)}</Badge></span>;
        return land === null ? NONE() : haUnit(land);
      },
    },
    { key: 'pairs', header: tr(m.colPairs), numeric: true, render: (f) => num(f.pairs) },
  ];
}

function canalColumns(t: ReturnType<typeof useT>): Column<Canal>[] {
  return [
    { key: 'canal', header: tr(m.colCanal), render: (c) => <strong>{tr(m.canalName, { label: c.label })}</strong> },
    { key: 'vol', header: tr(m.colVolume), unit: tr(UNIT_LABELS.m3), numeric: true, render: (c) => (vol(c.volume_m3) === null ? NONE() : num(Math.round(vol(c.volume_m3)!))) },
    { key: 'land', header: tr(m.colLand), unit: tr(UNIT_LABELS.ha), numeric: true, render: (c) => (vol(c.area_ha) === null ? NONE() : num(Math.round(vol(c.area_ha)!))) },
    { key: 'ev', header: tr(m.colEvents), numeric: true, render: (c) => num(c.events) },
    {
      key: 'farms',
      header: tr(m.colCanalFarms),
      width: '44%',
      render: (c) => (
        <ul className="wf-canal__farms">
          {c.farms.map((f, i) => (
            <li key={f.actor?.id ?? i}>
              {t(m.farmShort, {
                farm: (chunk) => (f.actor ? <Link to={`/farms/${f.actor.id}?tab=water`}>{chunk}</Link> : <>{chunk}</>),
                name: f.actor ? farmName(f) : tr(m.farmUnknown),
                volume: vol(f.volume_m3) === null ? NONE() : m3Compact(vol(f.volume_m3)),
              })}
            </li>
          ))}
          {c.more_farms > 0 ? <li className="t-caption">{tr(m.moreFarms, { farms: countOf(c.more_farms, WORDS.farm) })}</li> : null}
        </ul>
      ),
    },
  ];
}

function Weeks({ weeks }: { weeks: Detail['weeks'] }) {
  const t = useT();
  const data = weeks.map((w) => ({ label: dateShort(w.week_from), volume: vol(w.volume_m3) ?? 0, land: vol(w.area_ha) ?? 0 }));
  const volCfg: ChartConfig = { volume: { label: t(m.weeksVolumeTitle), token: '--data-water', forms: [tr(UNIT_LABELS.m3), tr(UNIT_LABELS.m3), tr(UNIT_LABELS.m3)] } };
  const landCfg: ChartConfig = { land: { label: t(m.weeksLandTitle), token: '--data-series-1', forms: [tr(UNIT_LABELS.ha), tr(UNIT_LABELS.ha), tr(UNIT_LABELS.ha)] } };
  const volTicks = niceTicks(Math.max(0, ...data.map((p) => p.volume)));
  const landTicks = niceTicks(Math.max(0, ...data.map((p) => p.land)));
  return (
    <div className="wf-two">
      <div>
        <h4 className="wv-h">{t(m.weeksVolumeTitle)}</h4>
        <ChartContainer config={volCfg} height={200} label={t(m.weeksVolumeAria)}>
          <BarChart data={data} margin={{ top: 8, right: 4, bottom: 0, left: 0 }} barCategoryGap="22%">
            <CartesianGrid vertical={false} strokeDasharray="3 4" />
            <XAxis dataKey="label" interval="equidistantPreserveStart" minTickGap={14} tickLine={false} axisLine={false} />
            <YAxis width={56} ticks={volTicks} domain={[0, volTicks.at(-1)!]} tickLine={false} axisLine={false} tickFormatter={(v: number) => m3Compact(v).replace(t(UNIT_LABELS.m3), '').trim()} />
            <Tooltip cursor={{ fill: 'var(--data-grid)', fillOpacity: 0.5 }} content={(p) => <ChartTooltipContent {...(p as object)} config={volCfg} title={(l) => t(m.weeksTip, { week: String(l) })} />} />
            <Bar dataKey="volume" fill="var(--color-volume)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ChartContainer>
      </div>
      <div>
        <h4 className="wv-h">{t(m.weeksLandTitle)}</h4>
        <ChartContainer config={landCfg} height={200} label={t(m.weeksLandAria)}>
          <BarChart data={data} margin={{ top: 8, right: 4, bottom: 0, left: 0 }} barCategoryGap="22%">
            <CartesianGrid vertical={false} strokeDasharray="3 4" />
            <XAxis dataKey="label" interval="equidistantPreserveStart" minTickGap={14} tickLine={false} axisLine={false} />
            <YAxis width={56} ticks={landTicks} domain={[0, landTicks.at(-1)!]} tickLine={false} axisLine={false} />
            <Tooltip cursor={{ fill: 'var(--data-grid)', fillOpacity: 0.5 }} content={(p) => <ChartTooltipContent {...(p as object)} config={landCfg} title={(l) => t(m.weeksTip, { week: String(l) })} />} />
            <Bar dataKey="land" fill="var(--color-land)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ChartContainer>
      </div>
    </div>
  );
}
